import { useCallback, useMemo, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import moment from 'moment'
import { rem } from 'polished'
import useAgeOptions from 'hooks/Search/useAgeOptions'
import { take } from 'lib/array/arrayUtils'
import { getChildrenAgesLimits } from 'lib/offer/occupancyUtils'
import Group from 'components/utils/Group'
import Select from 'components/Common/Form/Input/Select'
import DateOfBirthInput from 'components/Common/Form/Input/DateOfBirthInput'
import BodyText from 'LuxKit/Typography/BodyText'

export const AgeSelect = styled(Select)`
  border-radius: ${props => props.theme.borderRadius.S};
  min-width: ${rem(120)};
`

interface Props extends Omit<React.ComponentProps<typeof Select>, 'onChange' | 'defaultValue' | 'value'> {
  childId: number
  onChange: (childId: number, age: number, birthDate?: string) => void
  value?: number
  capacities: Array<App.RoomCapacity> | undefined
  occupancy: App.Occupants
  minInfantAge?: number
  maxInfantAge: number
  maxChildAge: number
  minChildAge?: number
  maxTeenagerAge: number
  className?: string
  childrenRequireDateOfBirth?: boolean
  departureDate?: string
  childrenAgeLabel: string
  showChildrenSelectLabel?: boolean
}

function OccupancyChildAgeSelect(props: Props) {
  const { childId, capacities, occupancy, maxChildAge, minChildAge, maxInfantAge, minInfantAge, maxTeenagerAge, onChange, value, className, childrenRequireDateOfBirth, departureDate, childrenAgeLabel, showChildrenSelectLabel, ...rest } = props
  const intl = useIntl()
  const [birthDateValue, setBirthDateValue] = useState<string | undefined>(occupancy.childrenBirthDate?.[childId])
  const ageOptions = useAgeOptions()

  const ageLimits = useMemo(() => (
    getChildrenAgesLimits(capacities, occupancy, childId, maxInfantAge, maxChildAge, maxTeenagerAge)
  ), [capacities, occupancy, childId, maxChildAge, maxInfantAge, maxTeenagerAge])

  const options = useMemo(() => {
    const minAge = Math.max(ageLimits.min, minChildAge ?? 0)
    return take(ageOptions, ageLimits.max - minAge + 1, minAge)
  }, [ageOptions, ageLimits, minChildAge])

  // disable invalid birth dates
  const { minDate, maxDate, refDate } = useMemo(() => {
    if (!childrenRequireDateOfBirth || !departureDate) return {}

    const minAdultAge = Math.max(maxTeenagerAge, maxChildAge) + 1

    const minDate = moment(departureDate).subtract(minAdultAge, 'years').add(1, 'days')
    const maxDate = moment(departureDate).subtract(minInfantAge || 0.5, 'years')

    return {
      minDate: minDate.toDate(),
      maxDate: maxDate.toDate(),
      refDate: new Date(departureDate),
    }
  }, [childrenRequireDateOfBirth, departureDate, minInfantAge, maxChildAge, maxTeenagerAge])

  const onSelectChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = parseInt(e.currentTarget.value, 10)

    // reset birth date if age is greater than maxInfantAge
    if (childrenRequireDateOfBirth) {
      setBirthDateValue(undefined)
      onChange(childId, selectedValue, undefined)
      return
    }

    onChange(childId, selectedValue, birthDateValue)
  }, [childId, onChange, birthDateValue, childrenRequireDateOfBirth])

  const onChangeBirthDate = useCallback((date?: string, isoDate?: string) => {
    if (
      childrenRequireDateOfBirth &&
      isoDate
    ) {
      const age = moment(departureDate).diff(isoDate, 'years')
      setBirthDateValue(isoDate)
      onChange(childId, age, isoDate)
    }
  }, [childId, onChange, childrenRequireDateOfBirth, departureDate])

  const requiredErrorMsg = intl.formatMessage({
    id: 'pBx0Se_UWtlqjhTKTSHH',
    defaultMessage: 'Please select child’s age',
    description: 'Validation error when child age is not selected in occupancy picker',
  })
  const selectPlaceholder = intl.formatMessage({
    id: 'wGdrR8Og2EyHnwyz7BOm',
    defaultMessage: 'Select',
    description: 'Placeholder text for child age dropdown in occupancy picker',
  })

  return (
    <Group
      direction="horizontal"
      tabletDirection="vertical"
      horizontalAlign="space-between"
      gap={12}
      className={className}
      verticalAlign="space-between"
      style={{ position: 'relative' }}
    >
      <div>
        <BodyText variant="medium">{intl.formatMessage({
          id: 'LYbK6Nr63Xj04wekRUtR',
          defaultMessage: 'Child {childNumber}',
          description: 'Label for individual child in occupancy age selection, e.g. Child 1',
        }, { childNumber: childId + 1 })}
        </BodyText>
        {showChildrenSelectLabel && <BodyText variant="medium" colour="light-foreground-alt">{childrenAgeLabel}</BodyText>}
      </div>
      {!childrenRequireDateOfBirth && <AgeSelect
        {...rest}
        onChange={onSelectChange}
        value={value}
        noValidationMessage
        noValidationSpacing
        requiredErrorMessage={requiredErrorMsg}
        placeholder={selectPlaceholder}
      >
        {options.map(age =>
          <option key={age.value} value={age.value}>{age.label}</option>,
        )}
      </AgeSelect>}

      {childrenRequireDateOfBirth && <DateOfBirthInput
        key={`birth-date-${value}`}
        max={maxDate}
        min={minDate}
        defaultValue={birthDateValue}
        onChange={onChangeBirthDate}
        refDate={refDate}
        travellerType="child"
        required
      />}
    </Group>
  )
}

export default OccupancyChildAgeSelect
